/**
 * Modal Component - Accessible modal with overlay, header, body, footer
 */

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { colors, spacing, borderRadius, zIndex } from '../../styles/design-tokens';

const Modal = ({
  open,
  onClose,
  children,
  className = '',
  title = '',
  description = '',
  footer = null,
  size = 'md',
  position = 'center',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  ...props
}) => {
  const overlayRef = useRef(null);

  // Sizes
  const sizes = {
    xs: 'max-w-sm',
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[90vw]',
  };

  // Positions
  const positions = {
    center: 'items-center',
    top: 'items-start pt-16',
    bottom: 'items-end pb-16',
  };

  // Close on escape
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose?.();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose, closeOnEscape]);

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && closeOnOverlayClick) {
      onClose?.();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex justify-center px-4 py-6 bg-ink/40 animate-fade-in"
      onClick={handleOverlayClick}
      style={{ zIndex: zIndex.modal }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
      {...props}
    >
      <div
        className={`
          relative bg-surface rounded-2xl shadow-2xl w-full
          animate-scale-in
          ${sizes[size] || sizes.md}
          ${positions[position] || positions.center}
          ${className}
          max-h-[90vh] flex flex-col
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 shrink-0">
            <div>
              {title && (
                <h2 id="modal-title" className="text-lg font-semibold text-ink">
                  {title}
                </h2>
              )}
              {description && (
                <p id="modal-description" className="text-sm text-muted mt-1">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-1 text-neutral-400 hover:text-ink transition-colors rounded-lg hover:bg-neutral-100"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

Modal.displayName = 'Modal';

export default Modal;